import { cartClient } from '../common/cartClient';
import type {
  AssociateOrderRequest,
  AssociateOrderResponse,
  EditBillingAddressRequest,
  EditBillingAddressResponse,
  EditShippingAddressRequest,
  EditShippingAddressResponse,
  GetOrderRequest,
  GetOrderResponse,
  GetOrdersRequest,
  GetOrdersResponse,
  GetUserOrdersRequest,
  GetUserOrdersResponse,
} from './utils/types';

/**
 * API client for Order-related operations
 */
class OrderAPIClient {
  /**
   * Get a single order by ID
   * @param params - Order request parameters
   * @returns Promise with order data
   */
  public async getOrder({ orderId, ...params }: GetOrderRequest): Promise<GetOrderResponse> {
    return await cartClient.get(`/orders/${orderId}`, { params }).then((res) => res.data);
  }

  /**
   * Get list of orders
   * @param params - Orders list request parameters
   * @returns Promise with orders list
   */
  public async getOrders(params?: GetOrdersRequest): Promise<GetOrdersResponse> {
    return await cartClient.get('/orders', { params }).then((res) => res.data);
  }

  /**
   * Get order IDs for a specific user
   * @param params - User ID request parameters
   * @returns Promise with order IDs list
   */
  public async getUserOrders({ userId }: GetUserOrdersRequest): Promise<GetUserOrdersResponse> {
    return await cartClient.get(`/orders/user/${userId}`).then((res) => res.data);
  }

  /**
   * Associate an order with the authenticated user
   * @param params - Order association request parameters
   * @returns Promise with association result
   */
  public async associateOrder({ orderId }: AssociateOrderRequest): Promise<AssociateOrderResponse> {
    return await cartClient.post('/orders/associate', { orderId }).then((res) => res.data);
  }

  /**
   * Edit shipping address for an order within 5 minutes of creation
   * Used for Express Checkout when required fields are missing
   * @param params - Shipping address update parameters
   * @returns Promise with edit result
   */
  public async editShippingAddress({
    orderId,
    ...shippingData
  }: EditShippingAddressRequest): Promise<EditShippingAddressResponse> {
    return await cartClient.patch(`/order/${orderId}/editShippingAddress`, shippingData).then((res) => res.data);
  }

  /**
   * Edit billing address for an order within 5 minutes of creation
   * Used for Express Checkout to update billing address with PayPal payer data
   * @param params - Billing address update parameters
   * @returns Promise with edit result
   */
  public async editBillingAddress({
    orderId,
    ...billingData
  }: EditBillingAddressRequest): Promise<EditBillingAddressResponse> {
    return await cartClient.patch(`/order/${orderId}/editBillingAddress`, billingData).then((res) => res.data);
  }
}

export const orderAPIClient = new OrderAPIClient();
